import { Box, Button, Grid, Input, styled } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { useImageContext } from '../../imageContext';
import { useGetToken } from '../../hooks/useGetToken';
import Context from '../../context';
import { fbDeleteImage, fbGetImageUrl } from '../../services/service';
import { usePopupContext } from '../../PopupContext';

const ButtonContainerBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: '20px',
  alignItems: 'center',
  width: '100%',
});

const ImageTopGrid = styled(Grid)({
  height: '200px',
  maxHeight: '200px',
  paddingRight: '10px',
  width: '90%',
});

const ImageGrid = styled(Grid)({
  height: '200px',
  maxHeight: '200px',
  border: '1px solid lightgray',
  borderRadius: '3px',
  width: '100%',
  objectFit: 'scale-down',
  paddingRight: '10px',
  backgroundColor: '#FFFFFF',
});

const ButtonStyled = styled(Button)({
  borderRadius: '24px',
  padding: '6px 50px',
});

const deleteImageTitle = 'Delete image';
const deleteImageMessage = 'Do you want to delete image';

export const ImageDetail = (): JSX.Element => {
  const {
    selected,
    setTimestamp,
    setSelected,
    imageUrl,
    setImageUrl,
    setListChanged,
  } = useImageContext();
  const { updateIsLoading, setShowError, setMessage: setErrorMessage } = useContext(Context);
  const [getTokenFunction] = useGetToken();

  const {
    setTrigger,
    setTitle,
    setMessage,
    setMethod,
    setAction,
    setExecuteMethod,
    executeMethod,
  } = usePopupContext();

  const deleteDisabled = selected === '' || selected.endsWith('/');
  const uploadDisabled = selected === '' || !selected.endsWith('/');

  if (selected === '' || selected.endsWith('/')) {
    setImageUrl('');
  }

  useEffect(() => {
    const getImageUrl = async () => {
      try {
        updateIsLoading(true);
        const accessToken = await getTokenFunction();
        const imageUrl = await fbGetImageUrl(selected, false, accessToken);
        if (imageUrl) {
          setImageUrl(imageUrl);
        }
      } catch (e) {
        setErrorMessage(`fbGetImageUrl ${e}`)
        setShowError(true);
      } finally {
        updateIsLoading(false);
      }
    };

    if (!deleteDisabled) {
      getImageUrl();
    }
  }, [selected]);

  const hasImage = imageUrl.length > 0;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files ? event.target.files[0] : '';
    const accessToken = await getTokenFunction();

    if (selectedFile) {
      updateIsLoading(true);
      const fileName = `${selected}${selectedFile.name}`;
      const imageUrl = (await fbGetImageUrl(fileName, true, accessToken)) || '';

      await fetch(imageUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: { 'Content-Type': selectedFile.type },
      });

      updateIsLoading(false);
      setImageUrl(imageUrl);
      setListChanged(fileName);
      setSelected(fileName);
    }
  };

  useEffect(() => {
    const execute = async () => {
      if (executeMethod === '--handleDeleteImage') {
        await handleDeleteImage();
        setExecuteMethod('');
      }
    };
    execute();
  }, [executeMethod]);

  const handleDeleteImage = async () => {
    const image = selected;
    const accessToken = await getTokenFunction();
    updateIsLoading(true);
    await fbDeleteImage(image, accessToken)
      .then(() => {
        setSelected('');
        setTimestamp(`${new Date().toISOString()}`);
        //      updateIsLoading(false);
      })
      .catch((e) => {
        setErrorMessage(`fbGetImageUrl ${e}`)
        setShowError(true);
        updateIsLoading(false);
      });
  };

  return (
    <>
      <ImageTopGrid container>
        <ImageGrid>
          <Box>
            {hasImage ? (
              <img
                src={imageUrl}
                style={{
                  height: '200px',
                  maxHeight: '200px',
                  border: '1px solid lightgray',
                  borderRadius: '3px',
                  width: '90%',
                  objectFit: 'scale-down',
                  paddingRight: '10px'
                }}
              />
            ) : (
              <br />
            )}
          </Box>
        </ImageGrid>
      </ImageTopGrid>
      <Grid container spacing={2} sx={{width:'90%'}}>
      <Input
              id="imagePicker"
              type="file"
              onChange={handleFileChange}
              disabled={false}
              style={{ display: 'none' }}
            />
          <ButtonContainerBox>
            <ButtonStyled
              disabled={deleteDisabled}
              data-testid="image-delete-button"
              variant="contained"
              onClick={() => {
                setTitle(deleteImageTitle);
                setMessage(`${deleteImageMessage} ${selected}?`);
                setMethod('handleDeleteImage');
                setAction('Delete');
                setTrigger(true);
              }}
            >
              Delete
            </ButtonStyled>
            <ButtonStyled
              disabled={uploadDisabled}
              data-testid="image-upload-button"
              variant="contained"
            >
              <label htmlFor="imagePicker" style={{ cursor: 'pointer' }}>
                Upload
              </label>
            </ButtonStyled>
          </ButtonContainerBox>
      </Grid>
    </>
  );
};
